import { useRef, useState, type FormEvent } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  StackLayout,
  HeadingText,
  ParagraphText,
  Button,
  TextAction,
  FormField,
  TextInput,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import { useUpdatePreferredName } from 'hooks/use-update-preferred-name/use-update-preferred-name.hook';
import {
  InfoSection,
  InfoValueText,
} from 'components/info-section/info-section.component';
import { SectionCard } from 'components/section-card/section-card.component';
import { LeaveWithoutSavingModal } from 'components/modals/leave-without-saving-modal/leave-without-saving-modal.component';

type PersonalInformationFields = {
  fullName: string;
  birthDate: string;
  preferredName: string;
};

// Edit mode: only Preferred name is editable; Full name and Birth date are
// shown disabled. Save persists the preferred name via set_user_profile.
const PersonalInformationForm = ({
  fullName,
  birthDate,
  preferredName: initialPreferredName,
  onClose,
}: PersonalInformationFields & { onClose: () => void }) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  const [preferredName, setPreferredName] = useState(initialPreferredName);
  const {
    mutate: updatePreferredName,
    isLoading: isSaving,
    isError: didSaveFail,
  } = useUpdatePreferredName();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updatePreferredName(preferredName.trim(), { onSuccess: onClose });
  };

  // Baseline captured once at mount (useRef initializer) — not the live
  // `initialPreferredName` prop — so a profile refetch mid-edit can't drift the
  // baseline and flip isDirty with no user input.
  const baselinePreferredName = useRef(initialPreferredName.trim());

  // Preferred name is the only editable field, so the form is "dirty" once it
  // differs from the seeded value. Trimmed compare so trailing whitespace the
  // save would drop doesn't count as an unsaved change.
  const isDirty = preferredName.trim() !== baselinePreferredName.current;

  return (
    <SectionCard>
      <StackLayout
        as="form"
        onSubmit={handleSubmit}
        spacing="$oneAndHalf"
        horizontalAlignment="stretch"
      >
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="spaceBetween"
          verticalAlignment="center"
          spacing="$one"
          css={{ width: '100%' }}
        >
          <HeadingText
            level="2"
            size="sm"
            css={{ typography: '$headingThree' }}
          >
            {formatMessage({ id: 'STR_PERSONAL_INFORMATION' })}
          </HeadingText>
          <TextAction
            as="button"
            type="button"
            icon="interfaceEdit"
            onClick={onClose}
            disabled={isSaving}
          >
            {formatMessage({ id: 'STR_CANCEL_EDIT' })}
          </TextAction>
        </StackLayout>

        {/*
          Genesis draws the border on the <input> itself, and its `:disabled`
          rule (borderColor $onSurfaceTextSubdued, #646464) outranks a plain
          css override — so target `input` with !important via the FormField.
          Disabled fields: border blends into the grey fill (no visible
          border). Editable field: $inputBorderDefault (#9A9A9A) per Figma.
        */}
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: isBelowTablet ? '1fr' : '1fr 1fr',
            gap: '$oneAndHalf',
          }}
        >
          <FormField
            id="full-name"
            label={formatMessage({ id: 'STR_FULL_NAME' })}
            css={{
              input: {
                borderColor: '$inputBackgroundDisabled !important',
              },
            }}
          >
            <TextInput
              value={fullName}
              disabled
              readOnly
              css={{ backgroundColor: '$inputBackgroundDisabled' }}
            />
          </FormField>
          <FormField
            id="preferred-name"
            label={formatMessage({ id: 'STR_PREFERRED_NAME' })}
            css={{
              input: {
                borderColor: '$inputBorderDefault !important',
              },
            }}
          >
            <TextInput
              value={preferredName}
              onChange={(event) => setPreferredName(event.target.value)}
              disabled={isSaving}
            />
          </FormField>
          <FormField
            id="birth-date"
            label={formatMessage({ id: 'STR_BIRTH_DATE' })}
            css={{
              input: {
                borderColor: '$inputBackgroundDisabled !important',
              },
            }}
          >
            <TextInput
              value={birthDate}
              disabled
              readOnly
              css={{ backgroundColor: '$inputBackgroundDisabled' }}
            />
          </FormField>
        </Box>

        {didSaveFail && (
          <ParagraphText role="alert" css={{ color: '$onSurfaceTextCritical' }}>
            {formatMessage({ id: 'STR_PREFERRED_NAME_SAVE_ERROR' })}
          </ParagraphText>
        )}

        {/*
          column-reverse on mobile keeps the DOM order (Cancel, Save) for tab
          order while rendering Save on top and Cancel below, per Figma.
        */}
        <Box
          css={{
            display: 'flex',
            flexDirection: isBelowTablet ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            gap: '$one',
            width: '100%',
          }}
        >
          <Button
            type="button"
            priority="secondary"
            onClick={onClose}
            disabled={isSaving}
            width={isBelowTablet ? 'fillContainer' : 'hugContents'}
          >
            {formatMessage({ id: 'STR_CANCEL_EDIT' })}
          </Button>
          <Button
            type="submit"
            priority="primary"
            loading={isSaving}
            loadingText={formatMessage({ id: 'STR_SAVING' })}
            disabled={isSaving}
            width={isBelowTablet ? 'fillContainer' : 'hugContents'}
          >
            {formatMessage({ id: 'STR_SAVE_CHANGES' })}
          </Button>
        </Box>
      </StackLayout>
      <LeaveWithoutSavingModal when={isDirty} />
    </SectionCard>
  );
};

// The Personal Information card. Toggles in place between a read-only view
// (with an Edit affordance) and the edit form — matching scan/cigna, no route
// change.
export const PersonalInformationSection = ({
  fullName,
  birthDate,
  preferredName,
}: PersonalInformationFields) => {
  const { formatMessage } = useIntl();
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <PersonalInformationForm
        fullName={fullName}
        birthDate={birthDate}
        preferredName={preferredName}
        onClose={() => setIsEditing(false)}
      />
    );
  }

  return (
    <InfoSection
      heading={formatMessage({ id: 'STR_PERSONAL_INFORMATION' })}
      onEdit={() => setIsEditing(true)}
      rows={[
        {
          id: 'full-name',
          label: formatMessage({ id: 'STR_FULL_NAME' }),
          value: fullName ? (
            <InfoValueText>{fullName}</InfoValueText>
          ) : undefined,
        },
        {
          id: 'preferred-name',
          label: formatMessage({ id: 'STR_PREFERRED_NAME' }),
          value: preferredName ? (
            <InfoValueText>{preferredName}</InfoValueText>
          ) : undefined,
        },
        {
          id: 'birth-date',
          label: formatMessage({ id: 'STR_BIRTH_DATE' }),
          value: birthDate ? (
            <InfoValueText>{birthDate}</InfoValueText>
          ) : undefined,
        },
      ]}
    />
  );
};
